import { guestApi, setGuestTokens, clearGuestTokens } from "../helpers/guest-axios.helper";
import {
  AvailabilityResponse,
  CreateGuestAppointmentDTO,
  EmailPreferences,
  GuestAppointment,
  GuestUser,
  LoginGuestDTO,
  PublicCategory,
  PublicCompany,
  PublicEmployee,
  PublicService,
  RegisterGuestDTO,
} from "../types/public-booking.type";

export const getPublicCompany = async (
  companyId: number | string
): Promise<PublicCompany> => {
  const { data } = await guestApi.get<PublicCompany>(`/companies/${companyId}`);
  return data;
};

export const getPublicServices = async (
  companyId: number | string,
  params: { category_id?: number; page?: number; limit?: number } = {}
): Promise<{ services: PublicService[]; pagination: any }> => {
  const { data } = await guestApi.get(`/companies/${companyId}/services`, { params });
  return data;
};

export const getPublicCategories = async (
  companyId: number | string
): Promise<PublicCategory[]> => {
  const { data } = await guestApi.get<{ categories: PublicCategory[] }>(
    `/companies/${companyId}/categories`
  );
  return data.categories;
};

export const getPublicEmployees = async (
  companyId: number | string,
  serviceId?: number
): Promise<PublicEmployee[]> => {
  const { data } = await guestApi.get<{ employees: PublicEmployee[] }>(
    `/employees/${companyId}`,
    { params: serviceId ? { service_id: serviceId } : {} }
  );
  return data.employees;
};

export const getPublicAvailability = async (
  companyId: number | string,
  params: { service_id: number; start_date?: string; end_date?: string; date?: string; employee_id?: number }
): Promise<AvailabilityResponse> => {
  const { data } = await guestApi.get<AvailabilityResponse>(
    `/companies/${companyId}/availability`,
    { params }
  );
  return data;
};

export const registerGuest = async (dto: RegisterGuestDTO) => {
  const { data } = await guestApi.post("/users/register", dto);
  await setGuestTokens(data);
  return data.user as GuestUser;
};

export const loginGuest = async (dto: LoginGuestDTO) => {
  const { data } = await guestApi.post("/users/login", dto);
  await setGuestTokens(data);
  return data.user as GuestUser;
};

export const logoutGuest = async () => {
  await clearGuestTokens();
};

export const getGuestProfile = async (): Promise<GuestUser> => {
  const { data } = await guestApi.get<GuestUser>("/users/me");
  return data;
};

export const getEmailPreferences = async (): Promise<EmailPreferences> => {
  const { data } = await guestApi.get<EmailPreferences>("/users/email-preferences");
  return data;
};

export const updateEmailPreferences = async (
  patch: Partial<EmailPreferences>
): Promise<EmailPreferences> => {
  const { data } = await guestApi.patch<EmailPreferences>("/users/email-preferences", patch);
  return data;
};

export const verifyGuestEmail = async (token: string) => {
  const { data } = await guestApi.get<{ message: string }>("/users/verify-email", {
    params: { token },
  });
  return data;
};

export const resendGuestVerification = async (email: string) => {
  const { data } = await guestApi.post<{ message: string }>("/users/resend-verification", {
    email,
  });
  return data;
};

export const createGuestAppointment = async (
  dto: CreateGuestAppointmentDTO
): Promise<GuestAppointment> => {
  const { data } = await guestApi.post<GuestAppointment>("/appointments", dto);
  return data;
};

export const listGuestAppointments = async (
  params: { page?: number; limit?: number } = {}
): Promise<{ appointments: GuestAppointment[]; pagination: any }> => {
  const { data } = await guestApi.get("/appointments", { params });
  return data;
};

export const getGuestAppointment = async (id: number): Promise<GuestAppointment> => {
  const { data } = await guestApi.get<GuestAppointment>(`/appointments/${id}`);
  return data;
};

export const cancelGuestAppointment = async (
  id: number,
  reason?: string
): Promise<GuestAppointment> => {
  const { data } = await guestApi.post<GuestAppointment>(`/appointments/${id}/cancel`, {
    reason,
  });
  return data;
};

export const rescheduleGuestAppointment = async (
  id: number,
  startTime: string
): Promise<GuestAppointment> => {
  const { data } = await guestApi.patch<GuestAppointment>(`/appointments/${id}`, {
    start_time: startTime,
  });
  return data;
};
